import {
    UiComponentDataContext, UiComponentPersistenceContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import {ReactElement} from "react";
import {GridItem, GridLayoutContainer, GridSize} from "../model/GridLayoutContainer";
import {Grid, GridSize as MuiGridSize} from "@mui/material";
import {UiComponent} from "../model/UiComponent";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";
import {t} from "../../../../../../store/localization/TranslMessasge";

export class GridLayoutContainerPlugin implements UiComponentRendererPlugin {
    getSupportedComponentTypes(): string[] {
        return ["GridLayoutContainer"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const gridLayoutContainer = component as GridLayoutContainer;
        const initialValuePromises = gridLayoutContainer.items
                .flatMap(item => this.getChildComponentsInitialValues(item.childComponents, dataContext, renderingContext));
        return Promise.all(initialValuePromises)
                .then((initialValues) => initialValues.reduce((a, b) => Object.assign(a, b), {}));
    }

    private getChildComponentsInitialValues(childComponents: UiComponent[], dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>>[] {
        return childComponents.map((child) => this.getChildComponentInitialValue(child, dataContext, renderingContext));
    }

    private getChildComponentInitialValue(child: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const plugin = renderingContext.rendererPluginRegistry.getPlugin(child.componentType);
        if (plugin) {
            return plugin.getInitialValues(child, dataContext, renderingContext);
        } else {
            return Promise.resolve({});
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const gridLayoutContainer = component as GridLayoutContainer;
        return gridLayoutContainer.items
                .map((item) => this.validateChildComponents(item.childComponents, dataContext, stateContext, validationContext))
                .reduce((a, b) => Object.assign(a, b), {});
    }

    private validateChildComponents(childComponents: UiComponent[], dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, any> {
        return childComponents.map((child) => this.validateChildComponent(child, dataContext, stateContext, validationContext))
                .reduce((a, b) => Object.assign(a, b), {});
    }

    private validateChildComponent(child: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, any> {
        const plugin = validationContext.pluginRegistry.getPlugin(child.componentType);
        if (plugin) {
            return plugin.validate(child, dataContext, stateContext, validationContext);
        } else {
            return {};
        }
    }

    renderComponent({component, stateContext, dataContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const gridLayoutContainer = component as GridLayoutContainer;

        return (
                <Grid container rowSpacing={2} columnSpacing={1}>
                    {gridLayoutContainer.items.map((item) => (
                        this.renderGridItem(item, stateContext, dataContext, renderingContext)
                    ))}
                </Grid>
        );
    }

    private renderGridItem(item: GridItem, stateContext: UiComponentStateContext, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): ReactElement {
        const gridProps = item.gridProperties;

        return (
                <Grid item
                        key={item.id}
                        xs={this.convertToMuiGridSize(gridProps.xs)}
                        sm={this.convertToMuiGridSize(gridProps.sm)}
                        md={this.convertToMuiGridSize(gridProps.md)}
                        lg={this.convertToMuiGridSize(gridProps.lg)}
                        xl={this.convertToMuiGridSize(gridProps.xl)}>
                    {item.childComponents.map((child) => {
                            const plugin = renderingContext.rendererPluginRegistry.getPlugin(child.componentType);
                            if (plugin) {
                                try {
                                    return plugin.renderComponent({
                                        component: child,
                                        parentComponent: item,
                                        stateContext: stateContext,
                                        dataContext: dataContext,
                                        renderingContext: renderingContext,
                                    });
                                } catch (e) {
                                    return (
                                            <ErrorFieldPlaceholder key={child.id} error={t(StereotypesTranslationKey.COMPONENT_RENDERING_ERROR)} />
                                    );
                                }
                            } else {
                                return (
                                        <div key={child.id}>Unknown component type: {child.componentType}</div>
                                );
                            }
                    })}
                </Grid>
        );
    }

    private convertToMuiGridSize(gridSize?: GridSize): boolean | MuiGridSize | undefined {
        if (gridSize === undefined) {
            return undefined;
        } else if (gridSize === "grow") {
            return true;
        } else if (gridSize === "auto") {
            return "auto";
        } else {
            return gridSize;
        }
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, persistenceContext: UiComponentPersistenceContext): Promise<void>[] {
        const gridLayoutContainer = component as GridLayoutContainer;
        return gridLayoutContainer.items
                .flatMap((item) => this.saveChildComponents(item.childComponents, dataContext, stateContext, persistenceContext));
    }

    private saveChildComponents(childComponents: UiComponent[], dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, persistenceContext: UiComponentPersistenceContext): Promise<void>[] {
        return childComponents.flatMap((child) => this.saveChildComponent(child, dataContext, stateContext, persistenceContext));
    }

    private saveChildComponent(child: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, persistenceContext: UiComponentPersistenceContext): Promise<void>[] {
        const plugin = persistenceContext.pluginRegistry.getPlugin(child.componentType);
        if (plugin) {
            return plugin.save(child, dataContext, stateContext, persistenceContext);
        } else {
            return [];
        }
    }

}